import React, { Fragment, useEffect, useState } from 'react'
import { Delete, Edit } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { usePagination } from '../../hooks/usePagination'
import { useApi } from '../../hooks/useApi'
import { useZones } from '../../hooks'
import { ApiResponse } from '../../typings/ApiResponse'
import { Zone } from '../../interfaces'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { deleteZone, getZoneList, updateZone } from './config/api'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Status } from '../../hooks/useFetchState'
import { getColumnsConfig } from './config/tableColumns'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../../components/_EOS/Modal'
import { CreateEditZoneModal } from './modal/CreateEditZoneModal'

interface ZonePageProps {}

const defaultFilters: Filter[] = [
  { key: 'active', label: 'Activo', value: 'true', displayValue: 'Sí' }
]

export const ZonePage: React.FC<ZonePageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<Zone>>()
  const { callApi: requestDeleteZone, state: deleteState } = useApi()
  const { callApi: requestStatusChange, state: statusChangeState } = useApi()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>(defaultFilters)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)

  // Cache de zonas en Redux que consume ZoneDDL; se refresca tras crear/editar/eliminar
  const { getZones: refreshZonesCache } = useZones()

  const [orderBy, setOrderBy] = useState<GenericOrderBy<Zone>>({
    field: 'zoneName' as keyof Zone,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (field: keyof Zone, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(
      getZoneList(pageToUse, pageSizeToUse, filtersToUse, orderBy)
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const resetAndFetchData = (filters?: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  // Refrescar solo cuando cambia el ordenamiento
  useEffect(() => {
    resetAndFetchData(selectedFilters)
  }, [orderBy])

  const createModal = useModal('Crear Zona')
  const editModal = useModal<Zone>('Editar Zona')
  const deleteModal = useModal<Zone>('Confirmar Eliminación')
  const statusChangeModal = useModal<Zone>('Confirmar cambio de estado')

  const handleDelete = (zoneId: number) => {
    requestDeleteZone(deleteZone(zoneId))
      .then(() => {
        openSnackBar('Zona eliminada exitosamente', true)
        deleteModal.closeModal()
        refreshZonesCache()
        resetAndFetchData()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleRequestStatusChange = (pendingZone: Zone) => {
    statusChangeModal.openModal('sm', pendingZone)
  }

  const handleConfirmStatusChange = () => {
    const pendingZone = statusChangeModal.extendedProps
    if (!pendingZone) return

    requestStatusChange(updateZone(pendingZone.zoneId, pendingZone))
      .then(() => {
        const successMessage = pendingZone.active
          ? 'Zona activada correctamente'
          : 'Zona desactivada correctamente'
        openSnackBar(successMessage, true)
        refreshZonesCache()
        resetAndFetchData()
        statusChangeModal.closeModal()
        editModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
        statusChangeModal.closeModal()
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('sm')
  }

  const statusChangeMessage = statusChangeModal.extendedProps?.active
    ? 'Al activar esta zona se activarán automáticamente todas las posiciones asociadas a ella. ¿Deseas continuar?'
    : 'Al inactivar esta zona se inactivarán automáticamente todas las posiciones asociadas a ella. ¿Deseas continuar?'

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={resetAndFetchData}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Zona'
        />
        <GenericTableV2<Zone>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='zoneId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof Zone,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('sm', item),
              icon: <Edit />
            },
            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <CreateEditZoneModal
          isEditing={false}
          closeModal={createModal.closeModal}
          onSuccess={resetAndFetchData}
          handleSnackbar={openSnackBar}
          onRequestStatusChange={handleRequestStatusChange}
        />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <CreateEditZoneModal
          zone={editModal.extendedProps}
          isEditing={true}
          closeModal={editModal.closeModal}
          onSuccess={resetAndFetchData}
          handleSnackbar={openSnackBar}
          onRequestStatusChange={handleRequestStatusChange}
        />
      </Modal>

      <Modal
        open={statusChangeModal.isOpen}
        maxWidth={statusChangeModal.size}
        title={statusChangeModal.title}
        onClose={statusChangeModal.closeModal}
        successButton={{
          action: handleConfirmStatusChange,
          title: 'Confirmar',
          disabled: statusChangeState.status === Status.Loading
        }}
        cancelButton={{
          action: statusChangeModal.closeModal,
          title: 'Cancelar',
          disabled: statusChangeState.status === Status.Loading
        }}
      >
        <span>{statusChangeMessage}</span>
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.zoneId || 0),
          title: 'Confirmar',
          disabled: deleteState.status === Status.Loading
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar',
          disabled: deleteState.status === Status.Loading
        }}
      >
        <span>
          ¿Desea eliminar la zona: <strong>{deleteModal.extendedProps?.zoneName}</strong>?
          Esta acción es definitiva y no puede revertirse.
        </span>
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
